import { notifyManager, queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { endServerSession, noteAccount } from '../lib/logout.ts';
import { MACHINE_LIST_MAX_AGE_MS, forgetMachines, rememberMachines, rememberedMachines, searchMachines } from '../lib/machine-cache.ts';
import { ApiRequestError, getJson, isOfflineError } from './client.ts';
import type { LookupUser, MachineDetail, MachineListItem, MachineLookupItem, Me, SavedView, SiteWithAreas, TeamListItem, TicketDetail } from './types.ts';

const ME_KEY = 'hd.me';

/** Letzte Anmeldung merken, damit die installierte App auch ohne Netz startet. */
function rememberMe(me: Me | null): void {
  try {
    if (me && !me.user.deviceId) localStorage.setItem(ME_KEY, JSON.stringify(me));
    else localStorage.removeItem(ME_KEY);
  } catch {
    /* privater Modus: dann eben ohne Offline-Start */
  }
}

export function forgetMe(): void {
  rememberMe(null);
  forgetMachines();
}

function rememberedMe(): Me | null {
  try {
    const raw = localStorage.getItem(ME_KEY);
    return raw ? (JSON.parse(raw) as Me) : null;
  } catch {
    return null;
  }
}

/**
 * Wer bin ich? 401 heißt „nicht angemeldet“ – kein Fehler, sondern ein Zustand.
 * Ohne Netz gilt die zuletzt bekannte Anmeldung (`offline: true`): Melden geht
 * dann in den Ausgangskorb und wird später gesendet.
 * Eine ausstehende Abmeldung kommt zuerst, denn `me` nennte sonst die vorherige
 * Person. Gelingt sie nicht, ist niemand angemeldet. Hier geht es nicht weiter zu
 * Microsoft: Auch das Formular der Schnellmeldung hängt an dieser Abfrage.
 */
export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async (): Promise<Me | null> => {
    if (!(await endServerSession())) return null;
    try {
      const me = await getJson<Me>('/api/auth/me');
      rememberMe(me);
      noteAccount(me.user.authSource);
      return me;
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) {
        rememberMe(null);
        return null;
      }
      if (isOfflineError(err)) {
        const cached = rememberedMe();
        if (cached) {
          noteAccount(cached.user.authSource);
          return { ...cached, offline: true };
        }
      }
      throw err;
    }
  },
  staleTime: 60_000,
  retry: false,
  // Kommt das Netz ohne `online`-Ereignis zurück, stünde „Kein Netz“ sonst bis zum nächsten Fokus.
  refetchInterval: (query) => (query.state.data?.offline ? 30_000 : false),
});

export const sitesQuery = queryOptions({
  queryKey: ['sites'],
  queryFn: () => getJson<SiteWithAreas[]>('/api/sites'),
  staleTime: 5 * 60_000,
});

function selectable<T extends { id: string; name: string; isActive: boolean }>(rows: T[], keep: Array<string | undefined>): T[] {
  return rows.flatMap((r) => (r.isActive ? [r] : keep.includes(r.id) ? [{ ...r, name: `${r.name} (stillgelegt)` }] : []));
}

/**
 * Werke und Bereiche für Auswahllisten: Stillgelegtes fällt heraus. `keep` nennt, was trotzdem stehen bleibt und
 * sich zu erkennen gibt: den gespeicherten Wert eines Formulars oder einen Filter aus einem Link. Ohne Eintrag
 * zeigte das Feld „Bitte wählen“, und der Wert wirkte unsichtbar weiter.
 */
export function selectableSites(sites: SiteWithAreas[] = [], keep: Array<string | undefined> = []): SiteWithAreas[] {
  return selectable(sites, keep).map((s) => ({ ...s, areas: selectable(s.areas, keep) }));
}

/** Für Auswahllisten ohne gespeicherten Wert. Teilt sich Cache und Neuladen mit `sitesQuery`. */
export const activeSitesQuery = queryOptions({
  ...sitesQuery,
  select: (sites: SiteWithAreas[]) => selectableSites(sites),
});

/**
 * Lädt die Werke neu und kehrt erst zurück, wenn die Seite sie zeigt: `invalidateQueries` endet vor der
 * Benachrichtigung der Abonnenten, `schedule` reiht sich dahinter ein. Wer danach etwas ins Bild rollt, misst
 * so die neue Seite, egal welcher Commit zuerst kommt.
 */
export async function refreshSites(qc: QueryClient): Promise<void> {
  await qc.invalidateQueries({ queryKey: sitesQuery.queryKey });
  await new Promise<void>((resolve) => notifyManager.schedule(resolve));
}

export const ticketQuery = (number: string) =>
  queryOptions({
    queryKey: ['ticket', number],
    queryFn: () => getJson<TicketDetail>(`/api/tickets/${number}`),
    refetchInterval: 60_000,
  });

export const machineQuery = (id: string) =>
  queryOptions({
    queryKey: ['machine', id],
    queryFn: () => getJson<MachineDetail>(`/api/machines/${id}`),
  });

/** Schlanke Maschinenliste, überdauert im Gerätespeicher das Neuladen ohne Netz. */
export const machineLookupQuery = queryOptions({
  queryKey: ['machine-lookup'],
  queryFn: async () => {
    const { items } = await getJson<{ items: MachineLookupItem[] }>('/api/machines/lookup');
    rememberMachines(items);
    return items;
  },
  initialData: () => rememberedMachines()?.items,
  initialDataUpdatedAt: () => rememberedMachines()?.fetchedAt,
  staleTime: MACHINE_LIST_MAX_AGE_MS,
});

function offlineSearchNote(hits: number, list: MachineLookupItem[] | undefined): string {
  if (!list?.length) return 'Kein Netz, und auf diesem Gerät liegt noch keine Maschinenliste. Bitte den QR-Code der Maschine scannen oder ohne Maschine melden.';
  if (!hits) return 'Kein Netz. In der Maschinenliste auf diesem Gerät gibt es dazu keinen Treffer. Anders schreiben, den QR-Code der Maschine scannen oder ohne Maschine melden.';
  return 'Kein Netz. Diese Treffer stammen aus der Maschinenliste auf diesem Gerät.';
}

/**
 * Maschinensuche der Meldewege: Ohne Antwort sucht sie in der Liste auf dem Gerät und sagt das. Das gilt schon
 * ab dem ersten Fehlschlag, denn die Wiederholungen dauern Sekunden. Nach einer Antwort zählt nur noch die.
 */
export function useMachineSearch(term: string, { pageSize, enabled = true }: { pageSize: number; enabled?: boolean }) {
  const list = useQuery(machineLookupQuery).data;
  const results = useQuery({
    queryKey: ['machines', 'search', term, pageSize],
    queryFn: () => getJson<{ items: MachineListItem[] }>('/api/machines', { q: term, pageSize }),
    enabled: enabled && term.length >= 2,
  });
  const offline = results.isPaused || isOfflineError(results.failureReason);
  const local = offline ? searchMachines(list ?? [], term, pageSize) : [];
  const items: (MachineLookupItem & { light?: MachineListItem['light'] })[] = results.data?.items ?? local;
  return {
    list,
    results,
    items,
    offline,
    searching: results.isFetching && !offline,
    note: offline && !results.data ? offlineSearchNote(items.length, list) : null,
  };
}

/** Teams (InvGate „Help Desks“) mit Zählern und Mitgliedern. */
export const teamsQuery = queryOptions({
  queryKey: ['teams'],
  queryFn: () => getJson<TeamListItem[]>('/api/teams'),
  staleTime: 60_000,
});

/** Teams für Auswahllisten: Das Team eines stillgelegten Werks fällt heraus, außer dort ist noch etwas offen oder `keep` zeigt darauf. */
export function selectableTeams(teams: TeamListItem[], sites: SiteWithAreas[] = [], keep?: string): TeamListItem[] {
  const retired = new Set(sites.filter((s) => !s.isActive).map((s) => s.id));
  return teams.filter((t) => !retired.has(t.siteId) || t.openCount > 0 || t.id === keep);
}

export const savedViewsQuery = queryOptions({
  queryKey: ['views'],
  queryFn: () => getJson<SavedView[]>('/api/views'),
  staleTime: 5 * 60_000,
});

export const workersQuery = (siteId?: string) =>
  queryOptions({
    queryKey: ['users', 'workers', siteId ?? 'all'],
    queryFn: () => getJson<LookupUser[]>('/api/users', { workers: true, siteId }),
    staleTime: 5 * 60_000,
  });
